// =============================================================================
// OnboardingEmailsAdmin — the "Automatic Emails" tab (Manage → System Content)
// =============================================================================
// Edits the automatic emails the portal sends. Each template is stored in
// `portal_config` and read server-side by the send-email edge function (with a
// code default as fallback). Two style tabs:
//   • Rich Text — the shelter-facing notifications, edited with the CRM email-safe
//     editor (HTML). {{link}} renders as a button.
//   • Plain Text — onboarding (access-code emails, kept plain for scanner-proofing)
//     + the internal invoicing / task / feedback notices.
// Editing CONTENT never needs a deploy — the function reads it fresh each send.
//
// Pulled out of AdminPages.jsx as part of the Admin file split (May 2026).
// =============================================================================
import React from "react";
import { EmailEditor } from "../../crm/compose/EmailEditor.jsx";
import { getConfigValues, upsertConfig } from "../../data/portalConfig";
import { listResourceDocuments } from "../../data/resourceDocuments";
import { Btn } from "../../utils/ui.jsx";

// Templates that can carry a Resources ATTACHMENT (central place — the attachment is
// a property of the template, so every send of it attaches, docs/welcome-email-rich-plan.md).
const ATTACHMENT_KEYS = new Set(["onboarding_caseworker_template", "onboarding_staff_template"]);

// Emails that use the rich (CRM email-safe) editor + HTML storage; everything else
// is plain text. The edge function renders stored HTML → sanitised HTML email, or
// stored plain text → the legacy auto-format (so old plain templates still work).
const RICH_KEYS = new Set([
  "request_received_template",
  "comment_to_shelter_template",
  "file_to_shelter_template",
  "picker_order_confirmation_template",
  "top_up_prepared_template",
]);

// Load a stored value into the rich editor: pass HTML through; convert a legacy
// plain-text template to simple HTML so it renders cleanly in the editor.
function plainToHtml(t) {
  const s = String(t || "");
  if (/<[a-z][\s\S]*>/i.test(s)) return s; // already HTML
  const esc = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc
    .split(/\n\n+/)
    .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

// Every editable automatic email. Defaults here MUST match the edge function's
// DEFAULT_* constants so "Reset to default" and the server agree.
const TEMPLATES = [
  {
    key: "onboarding_caseworker_template",
    label: "New Caseworker Welcome Email",
    description: "Sent automatically when a new caseworker account is created.",
    defaultText:
      'Hello {{name}},\n\nWelcome to the Zephyr Education Portal. Your account has been set up and you can now submit requests for educational supplies for the children in your care.\n\nTo get started, open the portal below and click "Have an access code?", then enter the code to set your password.\n\nAccess code: {{otp_code}}\nPortal: {{portal_url}}\nYour login email is: {{email}}\n\nEnter this code within 24 hours. If it has expired, ask a Zephyr admin to send you a new one.\n\nIf you have any questions, please contact the Zephyr Education team.\n\nKind regards\nZephyr Education Team',
    defaultSubject: "Welcome to the Zephyr Education Portal",
  },
  {
    key: "onboarding_staff_template",
    label: "New Zephyr Staff Welcome Email",
    description: "Sent automatically when a new Zephyr staff account is created.",
    defaultText:
      'Hello {{name}},\n\nYour Zephyr Education staff account has been created.\n\nTo get started, open the portal below and click "Have an access code?", then enter the code to set your password.\n\nAccess code: {{otp_code}}\nPortal: {{portal_url}}\nYour login email is: {{email}}\n\nEnter this code within 24 hours. If it has expired, ask a Zephyr admin to send you a new one.\n\nKind regards\nZephyr Education Team',
    defaultSubject: "Your Zephyr Education staff account",
  },
  {
    key: "password_reset_template",
    label: "Password Reset Email",
    description:
      'Sent when a Zephyr admin uses "Password Reset Email" on a user\'s edit form. Uses {{name}}, {{otp_code}}, {{portal_url}} and {{email}}. The user opens the portal, clicks "Have an access code?", and enters the code to set a new password.',
    defaultText:
      'Hello {{name}},\n\nA password reset has been requested for your Zephyr Education Portal account.\n\nTo set a new password, open the portal below and click "Have an access code?", then enter the code.\n\nAccess code: {{otp_code}}\nPortal: {{portal_url}}\nYour login email is: {{email}}\n\nEnter this code within 24 hours. If it has expired, ask a Zephyr admin to send you a new one. If you did not expect this email you can ignore it — your password will not change until the code is used.\n\nKind regards\nZephyr Education Team',
    defaultSubject: "Reset your Zephyr Education Portal password",
  },
  {
    key: "request_received_template",
    label: "Request Received Confirmation",
    description:
      "Sent to the caseworker automatically when they submit a new request. Uses {{ref_no}}, {{shelter_name}}, {{caseworker_name}} and {{link}} (the link becomes a button). If you leave {{link}} out, a link to the request is added automatically at the end.",
    defaultText:
      "Hello {{shelter_name}},\n\nWe have received your newly submitted Request {{ref_no}}. We will email you when we process the Request. No approval is implied by this email.\n\nYou can view your request here:\n\n{{link}}\n\nKind regards\nZephyr Education Team",
    defaultSubject: "Zephyr Education — We've received your Request {{ref_no}}",
  },
  {
    key: "comment_to_shelter_template",
    label: "New Comment Notification",
    description:
      "Sent to the DFV Service when Zephyr adds a comment on their request. Uses {{shelter_name}}, {{caseworker_name}}, {{ref_no}} and {{link}} (the link becomes a button).",
    defaultText:
      "Hello {{shelter_name}},\n\nA new message has been added to your Zephyr Education request {{ref_no}}.\n\nOpen your request to read it and reply if needed:\n{{link}}\n\nYour reference number is: {{ref_no}}\n\nPlease do not reply directly to this email — log in to the portal to respond.\n\nKind regards\nZephyr Education Team",
    defaultSubject: "Zephyr Education — New message on your request {{ref_no}}",
  },
  {
    key: "file_to_shelter_template",
    label: "New Document Notification",
    description:
      "Sent to the DFV Service when Zephyr adds a document on their request. Uses {{shelter_name}}, {{caseworker_name}}, {{ref_no}}, {{file_name}} and {{link}}.",
    defaultText:
      "Hello {{shelter_name}},\n\nA new document has been added to your Zephyr Education request {{ref_no}}.\n\nFile: {{file_name}}\n\nOpen your request to view and download it:\n{{link}}\n\nYour reference number is: {{ref_no}}\n\nPlease do not reply directly to this email — log in to the portal to respond.\n\nKind regards\nZephyr Education Team",
    defaultSubject: "Zephyr Education — New document on your request {{ref_no}}",
  },
  {
    key: "picker_order_confirmation_template",
    label: "Picker Order Confirmation",
    description:
      "Sent to the DFV Service when a picker completes an order. Uses {{shelter_name}}, {{caseworker_name}}, {{ref_no}}, {{item_type}} (Stationery/Device) and {{link}}.",
    defaultText:
      "Hello {{shelter_name}},\n\nYour {{item_type}} for request {{ref_no}} has been ordered.\n\nOpen your request for details:\n{{link}}\n\nKind regards\nZephyr Education Team",
    defaultSubject: "Zephyr Education — Your order for request {{ref_no}}",
  },
  {
    key: "top_up_prepared_template",
    label: "Stock Top-up Prepared (to DFV Service)",
    description:
      "Sent to a DFV Service's primary contact when Zephyr staff (or telemarketing) prepare a Stock Top-up for them. Uses {{shelter_name}} and {{link}} (the log-in link to review + submit it).",
    defaultText:
      "Hello {{shelter_name}},\n\nZephyr Education has prepared a Stock Top-up order for your service.\n\nPlease log in to the portal to review the order and submit it:\n{{link}}\n\nPlease do not reply directly to this email — log in to the portal to continue.\n\nKind regards\nZephyr Education Team",
    defaultSubject: "Zephyr Education — A Stock Top-up has been prepared for you",
  },
  {
    key: "new_invoice_template",
    label: "New Invoice Uploaded (to Invoicing)",
    description:
      "Sent to the invoicing inbox when an invoice is uploaded. Uses {{ref_no}}, {{shelter_name}}, {{file_name}}, {{message}} (the DFV Service's note) and {{link}}.",
    defaultText:
      "A new invoice has been uploaded for request {{ref_no}} ({{shelter_name}}).\n\nFile: {{file_name}}\n\nMessage from the DFV Service:\n{{message}}\n\nOpen the request to view it (sign in if prompted):\n{{link}}",
    defaultSubject: "Zephyr Request {{ref_no}} — New invoice uploaded",
  },
  {
    key: "comment_to_invoicing_template",
    label: "Comment Forwarded to Invoicing",
    description:
      "Sent to invoicing when a comment is forwarded. Uses {{ref_no}}, {{shelter_name}}, {{comment_author}}, {{comment_date}}, {{comment_text}}, {{link}}.",
    defaultText:
      "Comment forwarded to invoicing for request {{ref_no}} ({{shelter_name}}).\n\nFrom: {{comment_author}}\nDate: {{comment_date}}\n\n{{comment_text}}\n\nView request:\n{{link}}",
    defaultSubject: "Zephyr Request {{ref_no}} — Comment for Invoicing",
  },
  // NOTE (finding B6, 23 Aug 2026): `cancellation_invoicing_template` was REMOVED from
  // this list. There is no facility in the system for invoice cancellation — its caller
  // died with the Cancelled / Part Cancelled statuses retired in the June status-model
  // rework, so the template could never send. Do not reinstate it without a caller.
  {
    key: "task_assigned_template",
    label: "Task Assigned (to Staff)",
    description:
      "Sent to a staff member when a task is assigned to them. Uses {{ref_no}}, {{shelter_name}}, {{task_description}}, {{note}}, {{link}}.",
    defaultText:
      "You have been assigned a task on request {{ref_no}} ({{shelter_name}}).\n\nTask: {{task_description}}\n{{note}}\nView request:\n{{link}}",
    defaultSubject: "Task assigned to you — {{ref_no}}",
  },
  {
    key: "feedback_new_template",
    label: "New Feedback Submitted (to Admin)",
    description:
      "Sent to the feedback recipient when a worker submits feedback. Uses {{title}}, {{who}}, {{where}}, {{date_received}}, {{attachment}}, {{memo_excerpt}}, {{link}}.",
    defaultText:
      "New feedback has been submitted in the Zephyr Portal.\n\nTitle: {{title}}\nFrom: {{who}}\nShelter: {{where}}\nDate received: {{date_received}}\nAttachment: {{attachment}}\n\n{{memo_excerpt}}\n\nOpen the feedback to read it in full:\n{{link}}",
    defaultSubject: "Zephyr — New shelter feedback",
  },
  {
    key: "system_alert_intro",
    label: "System Alert — intro line",
    description:
      "Opening line of the 'a notification email failed to send' alert to Zephyr. The technical detail (type, request, error, what to do) is added automatically.",
    defaultText: "A Zephyr Portal notification email failed to send.",
  },
  {
    key: "sales_order_unrouted_intro",
    label: "Sales Order Needs Attention — intro line",
    description:
      "Opening line of the SO reconciliation alert. The money reconciliation, item lists and instructions are added automatically. {{ref_no}} available.",
    defaultText: "A Sales Order build at Confirm & Send did not fully reconcile — please check it.",
  },
  {
    key: "sales_order_push_failed_intro",
    label: "Sales Order Push Failed — intro line",
    description:
      "Opening line of the Unleashed push-failure alert (normal case). The failure list + instructions are added automatically; an AUTH failure uses a fixed critical message.",
    defaultText: "One or more Sales Orders failed to push to Unleashed after retrying.",
  },
];

const TABS = [
  { id: "rich", label: "Rich Text" },
  { id: "plain", label: "Plain Text" },
];

function tabOf(key) {
  return RICH_KEYS.has(key) ? "rich" : "plain";
}

// The default subject for a template key (only the shelter-facing emails have one).
function defaultSubjectFor(key) {
  return TEMPLATES.find((t) => t.key === key)?.defaultSubject || "";
}

export function OnboardingEmailsAdmin() {
  const [texts, setTexts] = React.useState({});
  // Editable subject line per template (only templates with a defaultSubject).
  const [subjects, setSubjects] = React.useState({});
  const [saving, setSaving] = React.useState(null);
  const [savedMsg, setSavedMsg] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  // Baseline (last-saved) text/subject per template — what Cancel reverts to.
  const [savedTexts, setSavedTexts] = React.useState({});
  const [savedSubjects, setSavedSubjects] = React.useState({});
  // Attachment config per template ({ enabled, document_id }) + its baseline, and the
  // PDF documents offered in the picker.
  const [attachments, setAttachments] = React.useState({});
  const [savedAttachments, setSavedAttachments] = React.useState({});
  const [pdfDocs, setPdfDocs] = React.useState([]);
  // Which templates are unlocked for editing (per-card, read-only until Edit).
  const [editing, setEditing] = React.useState({});
  const [activeTab, setActiveTab] = React.useState("rich");

  React.useEffect(function () {
    const keys = TEMPLATES.map((t) => t.key);
    Promise.all([getConfigValues(keys), listResourceDocuments()]).then(function (results) {
      const rows = results[0];
      const docs = results[1] || [];
      setPdfDocs(
        docs.filter(function (d) {
          return (d.mime_type || "").toLowerCase() === "application/pdf";
        })
      );
      const loaded = {};
      const loadedSubjects = {};
      const loadedAttachments = {};
      TEMPLATES.forEach(function (t) {
        const row = (rows || []).find((r) => r.key === t.key);
        const raw = row?.value?.text ? row.value.text : t.defaultText;
        loaded[t.key] = RICH_KEYS.has(t.key) ? plainToHtml(raw) : raw;
        if (t.defaultSubject) {
          loadedSubjects[t.key] =
            typeof row?.value?.subject === "string" && row.value.subject
              ? row.value.subject
              : t.defaultSubject;
        }
        if (ATTACHMENT_KEYS.has(t.key)) {
          const att = row?.value?.attachment;
          loadedAttachments[t.key] = {
            enabled: att ? att.enabled !== false : true, // default ON
            document_id: att?.document_id || "",
          };
        }
      });
      setTexts(loaded);
      setSavedTexts(loaded);
      setSubjects(loadedSubjects);
      setSavedSubjects(loadedSubjects);
      setAttachments(loadedAttachments);
      setSavedAttachments(loadedAttachments);
      setLoading(false);
    });
  }, []);

  function save(key) {
    setSaving(key);
    const text = texts[key];
    // Persist the subject alongside the body for templates that have one.
    const payload = defaultSubjectFor(key)
      ? { text: text, subject: subjects[key] || defaultSubjectFor(key) }
      : { text: text };
    // Persist the attachment config for templates that can carry one (central —
    // both send paths read it from here).
    if (ATTACHMENT_KEYS.has(key) && attachments[key]) {
      payload.attachment = {
        enabled: attachments[key].enabled !== false,
        document_id: attachments[key].document_id || "",
      };
    }
    upsertConfig(key, payload).then(function (r) {
      setSaving(null);
      if (r.error) {
        setSavedMsg(`Save failed: ${r.error.message}`);
      } else {
        setSavedTexts(function (prev) {
          return Object.assign({}, prev, { [key]: text });
        });
        setSavedSubjects(function (prev) {
          return Object.assign({}, prev, { [key]: payload.subject ?? prev[key] });
        });
        if (payload.attachment) {
          setSavedAttachments(function (prev) {
            return Object.assign({}, prev, { [key]: payload.attachment });
          });
        }
        setEditing(function (prev) {
          return Object.assign({}, prev, { [key]: false });
        });
        setSavedMsg("Saved");
      }
      setTimeout(function () {
        setSavedMsg("");
      }, 3000);
    });
  }

  function startEdit(key) {
    setSavedMsg("");
    setEditing(function (prev) {
      return Object.assign({}, prev, { [key]: true });
    });
  }

  function cancelEdit(key) {
    setTexts(function (prev) {
      return Object.assign({}, prev, { [key]: savedTexts[key] });
    });
    setSubjects(function (prev) {
      return Object.assign({}, prev, { [key]: savedSubjects[key] });
    });
    if (ATTACHMENT_KEYS.has(key)) {
      setAttachments(function (prev) {
        return Object.assign({}, prev, { [key]: savedAttachments[key] });
      });
    }
    setEditing(function (prev) {
      return Object.assign({}, prev, { [key]: false });
    });
  }

  if (loading) return <div className="text-zephyr-muted p-5">Loading...</div>;

  const visible = TEMPLATES.filter((t) => tabOf(t.key) === activeTab);

  return (
    <div>
      {/* Style tabs — Rich (shelter-facing) vs Plain (onboarding + internal). */}
      <div className="flex gap-2 mb-4">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            type="button"
            onClick={() => setActiveTab(tb.id)}
            className={[
              "rounded-[7px] py-[7px] px-[16px] text-[13px] font-semibold cursor-pointer border",
              activeTab === tb.id
                ? "bg-zephyr-teal-dark text-white border-zephyr-teal-dark"
                : "bg-white text-zephyr-text border-zephyr-border",
            ].join(" ")}
            style={{ fontFamily: "inherit" }}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <p className="m-0 mb-2 text-[13px] text-zephyr-muted">
        {activeTab === "rich"
          ? "Shelter-facing emails — edit with the rich-text editor (bold, lists, links). Type "
          : "Onboarding + internal notices — plain text. Editing content is instant (no deploy). Use "}
        {"{{link}}"} where you want the button to the request. Click Edit on a template, then Save
        Template.
      </p>
      <div className="bg-zephyr-off-white rounded-[8px] py-[10px] px-[14px] mb-6 text-xs text-zephyr-muted">
        {activeTab === "rich" ? (
          <>
            <strong>Shelter emails:</strong> {"{{shelter_name}}"} — DFV Service &nbsp;·&nbsp;
            {"{{caseworker_name}}"} / {"{{caseworker_first_name}}"} — recipient's full / first name
            &nbsp;·&nbsp;
            {"{{ref_no}}"} — request reference &nbsp;·&nbsp;
            {"{{link}}"} — button to the request &nbsp;·&nbsp;
            {"{{file_name}}"} — document name (Document email) &nbsp;·&nbsp;
            {"{{item_type}}"} — "Stationery" or "Device" (Picker email).
            <br />
            The <strong>Subject line</strong> is editable too and accepts the same tokens (e.g.{" "}
            {"{{ref_no}}"}).
          </>
        ) : (
          <>
            <strong>Onboarding:</strong> {"{{name}}"}, {"{{email}}"}, {"{{otp_code}}"} (access
            code), {"{{portal_url}}"} (plain link), {"{{shelter_name}}"}, {"{{caseworker_name}}"}/
            {"{{caseworker_first_name}}"} (the recipient's full / first name).
            <br />
            <strong>Invoicing / Task / Feedback:</strong> {"{{ref_no}}"}, {"{{shelter_name}}"},{" "}
            {"{{caseworker_name}}"}/{"{{caseworker_first_name}}"} (recipient — blank on the
            invoicing-inbox emails) &nbsp;·&nbsp; {"{{link}}"} &nbsp;·&nbsp; {"{{file_name}}"}/
            {"{{message}}"} (invoice) &nbsp;·&nbsp;
            {"{{comment_author}}"}/{"{{comment_date}}"}/{"{{comment_text}}"} (invoicing comment)
            &nbsp;·&nbsp; {"{{reason}}"} (cancellation) &nbsp;·&nbsp;
            {"{{task_description}}"}/{"{{note}}"} (task) &nbsp;·&nbsp;
            {"{{title}}"}/{"{{who}}"}/{"{{where}}"}/{"{{memo_excerpt}}"} (feedback).
            <br />
            The <strong>Subject line</strong> is editable too (except the three alert "intro line"
            entries) and accepts the same tokens.
          </>
        )}
      </div>
      {savedMsg && <div className="text-[#2E7D32] font-semibold text-[13px] mb-3">{savedMsg}</div>}
      {visible.map((t) => (
        <div
          key={t.key}
          className="mb-8 border border-zephyr-border rounded-[10px] overflow-hidden"
        >
          <div className="bg-zephyr-teal-dark text-white py-3 px-[18px]">
            <div className="font-bold text-[15px]">{t.label}</div>
            <div className="text-xs opacity-80 mt-0.5">{t.description}</div>
          </div>
          <div className="p-[18px]">
            {t.defaultSubject && (
              <div className="mb-3">
                <label
                  htmlFor={`subj-${t.key}`}
                  className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
                >
                  Subject line
                </label>
                <input
                  id={`subj-${t.key}`}
                  value={subjects[t.key] || ""}
                  disabled={!editing[t.key]}
                  onChange={function (e) {
                    const val = e.target.value;
                    setSubjects(function (prev) {
                      return Object.assign({}, prev, { [t.key]: val });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
            )}
            {RICH_KEYS.has(t.key) ? (
              <EmailEditor
                value={texts[t.key] || ""}
                readOnly={!editing[t.key]}
                onChange={function (html) {
                  setTexts(function (prev) {
                    return Object.assign({}, prev, { [t.key]: html });
                  });
                }}
              />
            ) : (
              <textarea
                value={texts[t.key] || ""}
                disabled={!editing[t.key]}
                onChange={function (e) {
                  const val = e.target.value;
                  setTexts(function (prev) {
                    return Object.assign({}, prev, { [t.key]: val });
                  });
                }}
                rows={12}
                className="w-full py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[13px] font-mono resize-y box-border text-zephyr-text bg-white leading-[1.6] disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
              />
            )}
            {ATTACHMENT_KEYS.has(t.key) && (
              <div className="mt-3 border-t border-zephyr-border pt-3">
                <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] mb-1">
                  Attachment (PDF)
                </div>
                <div className="text-[11px] text-zephyr-muted mb-2">
                  Attached to every send of this email — the invitation AND the account-creation
                  welcome. Choose a PDF from Resources; the tick (default on) lets you send without
                  it for a one-off without losing the choice.
                </div>
                <div className="flex gap-3 items-center flex-wrap">
                  <select
                    aria-label="Attachment PDF"
                    value={attachments[t.key]?.document_id || ""}
                    disabled={!editing[t.key]}
                    onChange={function (e) {
                      const val = e.target.value;
                      setAttachments(function (prev) {
                        return Object.assign({}, prev, {
                          [t.key]: Object.assign({}, prev[t.key], { document_id: val }),
                        });
                      });
                    }}
                    className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] bg-white max-w-[320px] disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                    style={{ fontFamily: "inherit" }}
                  >
                    <option value="">— No attachment —</option>
                    {pdfDocs.map(function (d) {
                      return (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      );
                    })}
                  </select>
                  <label
                    className={`flex items-center gap-2 text-[13px] cursor-pointer ${
                      attachments[t.key]?.document_id ? "text-zephyr-text" : "text-zephyr-muted"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={attachments[t.key]?.enabled !== false}
                      disabled={!editing[t.key] || !attachments[t.key]?.document_id}
                      onChange={function (e) {
                        const val = e.target.checked;
                        setAttachments(function (prev) {
                          return Object.assign({}, prev, {
                            [t.key]: Object.assign({}, prev[t.key], { enabled: val }),
                          });
                        });
                      }}
                    />
                    Attach file
                  </label>
                </div>
              </div>
            )}
            <div className="flex gap-[10px] mt-[10px] items-center">
              {editing[t.key] ? (
                <>
                  <Btn
                    onClick={function () {
                      save(t.key);
                    }}
                    disabled={saving === t.key}
                  >
                    {saving === t.key ? "Saving..." : "Save Template"}
                  </Btn>
                  <button
                    type="button"
                    onClick={function () {
                      cancelEdit(t.key);
                    }}
                    className="bg-white border border-zephyr-border rounded-[7px] py-[7px] px-[14px] text-[13px] font-semibold cursor-pointer text-zephyr-text"
                    style={{ fontFamily: "inherit" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={function () {
                      const def = RICH_KEYS.has(t.key) ? plainToHtml(t.defaultText) : t.defaultText;
                      setTexts(function (prev) {
                        return Object.assign({}, prev, { [t.key]: def });
                      });
                      if (t.defaultSubject) {
                        setSubjects(function (prev) {
                          return Object.assign({}, prev, { [t.key]: t.defaultSubject });
                        });
                      }
                    }}
                    className="bg-transparent border border-zephyr-border rounded-[7px] py-[7px] px-[14px] text-[13px] cursor-pointer text-zephyr-muted"
                    style={{ fontFamily: "inherit" }}
                  >
                    Reset to default
                  </button>
                </>
              ) : (
                <Btn
                  onClick={function () {
                    startEdit(t.key);
                  }}
                >
                  Edit
                </Btn>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
